import React, { useState } from 'react';
import styled from 'styled-components';
import { NextPage, GetServerSideProps } from 'next';
import Image from 'next/image';
import { InView } from 'react-intersection-observer';
import Cookies from 'js-cookie';
import { Button } from '@makeship/core';

import posthog from 'posthog-js';
import { useStore } from '../../../store';
import Head from '../../../components/Head';
import Container from '../../../components/Container';
import BadgeCard from '../../../components/BadgeCard';
import BadgesModal from '../../../components/Modal/BadgesModal';
import ShareModal from '../../../components/Modal/ShareModal';
import Loader from '../../../components/Loader';
import { S1, P2, H5Styles } from '../../../components/Typography';

import { getBadges } from '../../../api/storefront/badge';
import { ReactComponent as ShareIcon } from '../../../../public/assets/icons/share-icon.svg';

import config from '../../../../config.json';
import { accessTokenKey } from '../../../utils/cookies';
import { isUserLoggedIn } from '../../../utils/analytics';
import { getPropsForPageLoad } from '../../../utils/pageLoad';

const { routes } = config;

export const BadgesPageWrapper = styled(Container)`
  padding: 48px 64px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 24px 16px;
  }
`;

const BadgeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 36px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    align-items: start;
  }
`;

export const Title = styled.h1`
  ${H5Styles};
  margin-bottom: 38px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-bottom: 8px;
  }
`;

const BadgesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const EmptyBadgeContainer = styled.div`
  display: block;
  text-align: center;
`;

const ImageWrapper = styled.div`
  max-width: 236px;
  margin: 24px auto;
`;

const EmptyBadgesSubtitle = styled(S1)`
  margin-bottom: 8px;
`;

const ShareBadgeButton = styled(Button.Primary)``;

export const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

type BadgesPageProps = {
  badges: Badge[];
  username: string;
};

const BadgesPage: NextPage<BadgesPageProps> = ({ badges, username }: BadgesPageProps) => {
  const { state } = useStore();
  const [currentBadges, setCurrentBadges] = useState(badges);
  const [canLoadMore, setCanLoadMore] = useState(badges.length !== 0);
  const [isBadgesModalOpen, setIsBadgesModalOpen] = useState(true);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleModal = () => {
    setIsBadgesModalOpen(false);
    window.localStorage.setItem('seenBadgeEducationModal', 'true');
  };

  const loadMore = async (offset: number) => {
    setLoading(true);

    if (!username) {
      return;
    }

    const accessToken = Cookies.get(accessTokenKey);
    try {
      const badges = await getBadges({
        username,
        customerAccessToken: accessToken,
        offset: offset.toString(10),
      }).then((res) => res.data);

      if (badges?.value.length === 0) {
        setCanLoadMore(false);
      } else {
        const newBadges = currentBadges.concat(badges.value);
        setCurrentBadges(newBadges);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShareModal = () => {
    posthog.capture('badgeLibrary_shareLibrary_click', { username, is_logged_in: isUserLoggedIn(state.user) });
    setIsShareModalOpen(true);
  };

  return (
    <>
      <Head
        title="Visit this Badge Library"
        description="View all the Makeship Campaigns I’ve supported and sign up to claim your Badges."
        url={`${routes.badgeLibrary}/${encodeURIComponent(username || '')}`}
        image={`${process.env.NEXT_PUBLIC_DOMAIN}/assets/badges/makeship-badges.png`}
      />
      {username && (
        <BadgesPageWrapper>
          <BadgeHeader>
            <Title>{username}&apos;s Badges</Title>
            <ShareBadgeButton onClick={handleShareModal}>
              <IconWrapper>
                <ShareIcon />
              </IconWrapper>
              Share Library
            </ShareBadgeButton>
          </BadgeHeader>
          {currentBadges.length !== 0 ? (
            <BadgesWrapper>
              {currentBadges.map((badge) => (
                <BadgeCard key={badge.id} badge={badge} />
              ))}
            </BadgesWrapper>
          ) : (
            <EmptyBadgeContainer>
              <ImageWrapper>
                <Image
                  src="/assets/badges/empty-badge.png"
                  layout="responsive"
                  width="236px"
                  height="268px"
                  alt="empty badge image"
                />
              </ImageWrapper>
              <EmptyBadgesSubtitle>The Badge Library is currently empty</EmptyBadgesSubtitle>
              <P2>Support a Campaign to start collecting</P2>
            </EmptyBadgeContainer>
          )}
        </BadgesPageWrapper>
      )}
      {canLoadMore && (
        <InView as="div" onChange={(inView, entry) => inView && canLoadMore && loadMore(currentBadges.length)}>
          {loading && <Loader />}
        </InView>
      )}
      {typeof window !== 'undefined' && localStorage.getItem('seenBadgeEducationModal') !== 'true' && (
        <BadgesModal
          isOpen={isBadgesModalOpen}
          closeModal={() => {
            handleModal();
          }}
        />
      )}
      <ShareModal
        isOpen={isShareModalOpen}
        closeModal={() => {
          setIsShareModalOpen(false);
        }}
        username={username}
        hidden={state.user?.storefrontCustomer?.hidden}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { req, params } = ctx;
  const { cookies } = req;

  const username = params ? (params.username as string) : '';

  const badges = await getBadges({ username, customerAccessToken: cookies.accessToken }).then((res) => res.data);

  if (!badges) {
    return {
      notFound: true,
    };
  }

  const propsForPageLoad = await getPropsForPageLoad(ctx);

  return {
    props: { badges: badges.value, username: badges.username, ...propsForPageLoad },
  };
};

export default BadgesPage;
